/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { Role } from "../../Team/types";
import { templateCode, type DayTemplate, type Templates } from "../types";
import { TemplateEditor } from "./TemplateEditor";
import { WeekdayAssign } from "./WeekdayAssign";

// Day templates and which one each weekday uses.
// See docs/plans/workday/staff_planning.md (phase 1b).
export const RotaTemplatesPage = defineComponent({
  name: "RotaTemplatesPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const data = ref<Templates | null>(null);
    const roles = ref<Role[]>([]);
    const editing = ref<DayTemplate | null | undefined>(undefined);

    const load = async () => {
      try {
        const [tpls, r] = await Promise.all([trpc.rota.templates.list.query(), trpc.rota.roles.list.query()]);
        data.value = tpls as Templates;
        roles.value = r as Role[];
      } catch {
        toast.showToast(t("workday.common.load_failed"), { type: "error" });
      }
    };
    onMounted(load);

    const assigned = computed(() => Object.fromEntries((data.value?.weekdays ?? []).map((w) => [w.weekday, w.template_id])));
    const roleName = (id: number) => roles.value.find((r) => r.id === id)?.name ?? "?";

    const assign = async (weekday: number, templateId: number | null) => {
      if (data.value) {
        const others = data.value.weekdays.filter((w) => w.weekday !== weekday);
        data.value = { ...data.value, weekdays: templateId ? [...others, { weekday, template_id: templateId }] : others };
      }
      try {
        await trpc.rota.templates.setWeekdays.mutate({ [String(weekday)]: templateId });
      } catch {
        toast.showToast(t("workday.common.save_failed"), { type: "error" });
        await load();
      }
    };

    return () => (
      <div class="space-y-6">
        {!data.value ? (
          <div class="flex justify-center p-10">
            <Loading />
          </div>
        ) : (
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_24rem]">
            <div class="space-y-3">
              <div class="flex items-center justify-between">
                <h2 class="text-lg font-semibold text-accent">{t("workday.rota.templates_title")}</h2>
                <button class="btn btn-primary btn-sm" onClick={() => (editing.value = null)}>
                  <span class="material-symbols-outlined text-base">add</span>
                  {t("workday.rota.template_new")}
                </button>
              </div>
              {data.value.templates.length === 0 ? (
                <div class="rounded-2xl border border-dashed border-base-300 bg-base-100 p-8 text-center text-sm text-base-content/60">
                  {t("workday.rota.templates_empty")}
                </div>
              ) : (
                <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {data.value.templates.map((tpl) => (
                    <button key={tpl.id} class="rounded-xl border border-base-300 bg-base-100 p-4 text-left shadow-sm transition hover:border-primary" onClick={() => (editing.value = tpl)}>
                      <div class="flex items-center justify-between gap-2">
                        <span class="font-semibold">{tpl.name}</span>
                        <span class="badge badge-neutral badge-sm font-mono">{templateCode(tpl)}</span>
                      </div>
                      <ul class="mt-3 space-y-1.5 text-sm">
                        {tpl.blocks.map((b) => (
                          <li key={b.id} class="flex items-start gap-2">
                            <span class="w-24 shrink-0 font-medium tabular-nums">{`${b.start_time}–${b.end_time}`}</span>
                            <span class="text-base-content/60">
                              {b.slots.length ? b.slots.map((s) => `${roleName(s.role_id)} × ${s.headcount}`).join(", ") : "—"}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <div class="mt-3 text-xs text-base-content/50">{t("workday.rota.shift_count").replace("{count}", String(tpl.slot_count))}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {data.value.templates.length > 0 && (
              <WeekdayAssign templates={data.value.templates} assigned={assigned.value} onAssign={assign} />
            )}
          </div>
        )}
        {editing.value !== undefined && (
          <TemplateEditor
            template={editing.value}
            roles={roles.value}
            onClose={async (changed: boolean) => {
              editing.value = undefined;
              if (changed) await load();
            }}
          />
        )}
      </div>
    );
  },
});
